import React, { useState, useEffect } from 'react';
import { Row, Col, Spin, Alert, Button, Card, Statistic, Progress, Table, Avatar, Empty, Tooltip } from 'antd';
import { ReloadOutlined, UserOutlined, ShoppingCartOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import StatisticsApiService from '../../services/StatisticsApiService';

const StaffStatisticsDashboard = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await StatisticsApiService.getStaffStatistics();
      setStatistics(response);
    } catch (err) {
      setError('Failed to load statistics. Please try again.');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '20px' }}>Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" danger onClick={fetchStatistics}>
            Retry
          </Button>
        }
      />
    );
  }

  // Helper function to safely get data from different possible structures
  const getData = (path) => {
    if (!statistics) return 0;
    
    // Try different possible data structures
    const paths = [
      path,
      `data.${path}`,
      `overview.${path}`,
      `data.overview.${path}`
    ];
    
    for (const dataPath of paths) {
      const value = dataPath.split('.').reduce((obj, key) => obj?.[key], statistics);
      if (value !== undefined && value !== null) {
        return value;
      }
    }
    
    return 0;
  };

  const totalLearners = getData('totalLearners');
  const totalOrders = getData('totalOrders');
  const completedOrders = getData('orderStatusBreakdown.completed');
  const pendingOrders = getData('orderStatusBreakdown.pending');
  const failedOrders = getData('orderStatusBreakdown.failed');
  const canceledOrders = getData('orderStatusBreakdown.cancelled') || getData('orderStatusBreakdown.canceled');

  // Helper lấy mostPurchasedCourses từ data
  const getMostPurchasedCourses = () => {
    if (!statistics) return [];
    const paths = [
      'mostPurchasedCourses',
      'data.mostPurchasedCourses',
      'overview.mostPurchasedCourses',
      'data.overview.mostPurchasedCourses',
    ];
    for (const path of paths) {
      const value = path.split('.').reduce((obj, key) => obj?.[key], statistics);
      if (value && Array.isArray(value) && value.length > 0) {
        return value;
      }
    }
    return [];
  };

  const mostPurchasedCourses = getMostPurchasedCourses();

  const columns = [
    {
      title: '',
      dataIndex: 'courseImageUrl',
      key: 'image',
      render: url => <Avatar shape="square" size={48} src={url} />,
      width: 60,
      align: 'center',
    },
    {
      title: 'Tên khóa học',
      dataIndex: 'courseName',
      key: 'name',
      render: (text) => <b>{text}</b>,
    },
    {
      title: 'Danh mục',
      dataIndex: ['category', 'name'],
      key: 'category',
      render: (text, record) => record.category?.name || '',
      width: 120,
    },
    {
      title: 'Lượt mua',
      dataIndex: 'purchaseCount',
      key: 'purchaseCount',
      align: 'center',
      width: 90,
      render: (val) => <span style={{ fontWeight: 600, color: '#6c63ff' }}>{val}</span>
    },
    {
      title: 'Doanh thu',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right',
      width: 120,
      render: value => value?.toLocaleString('vi-VN') + ' ₫',
    },
  ];

  return (
    <div className="statistics-dashboard">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <h2>Staff Statistics Dashboard</h2>
        <Button 
          type="primary" 
          icon={<ReloadOutlined />} 
          onClick={fetchStatistics}
          loading={loading}
          className='add-btnw'
        >
          Refresh Data
        </Button>
      </div>

      {/* Overview Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Total Learners"
                  value={totalLearners}
                  valueStyle={{ 
                    color: '#1890ff', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Total Orders"
                  value={totalOrders}
                  valueStyle={{ 
                    color: '#52c41a', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <ShoppingCartOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Completed Orders"
                  value={completedOrders}
                  valueStyle={{ 
                    color: '#fa8c16', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <CheckCircleOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card className="overview-stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Statistic
                  title="Pending Orders"
                  value={pendingOrders}
                  valueStyle={{ 
                    color: '#f5222d', 
                    fontSize: '28px', 
                    fontWeight: 'bold' 
                  }}
                />
              </div>
              <div style={{ marginLeft: '16px' }}>
                <ClockCircleOutlined style={{ fontSize: '24px', color: '#f5222d' }} />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 2 cột: Order Status Overview & Most Purchased Courses */}
      <Row gutter={24} style={{ marginTop: 24 }}>
        <Col xs={24} md={12}>
          <Card
            title="Order Status Overview"
            style={{ borderRadius: 12, boxShadow: '0 2px 8px #f0f1f2', height: '100%' }}
            bodyStyle={{ minHeight: 350 }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card style={{ background: '#f6fff6', borderColor: '#52c41a', borderRadius: 12, textAlign: 'center', borderWidth: 2 }} bordered>
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 32 }} />
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a', margin: '8px 0' }}>{completedOrders}</div>
                  <div>Completed</div>
                  <div style={{ color: '#52c41a' }}>{totalOrders > 0 ? Math.round((completedOrders/totalOrders)*100) : 0}%</div>
                </Card>
              </Col>
              <Col span={12}>
                <Card style={{ background: '#fffbe6', borderColor: '#faad14', borderRadius: 12, textAlign: 'center', borderWidth: 2 }} bordered>
                  <ClockCircleOutlined style={{ color: '#faad14', fontSize: 32 }} />
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#faad14', margin: '8px 0' }}>{pendingOrders}</div>
                  <div>Pending</div>
                  <div style={{ color: '#faad14' }}>{totalOrders > 0 ? Math.round((pendingOrders/totalOrders)*100) : 0}%</div>
                </Card>
              </Col>
              <Col span={12}>
                <Card style={{ background: '#fff1f0', borderColor: '#ff4d4f', borderRadius: 12, textAlign: 'center', borderWidth: 2 }} bordered>
                  <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 32 }} />
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#ff4d4f', margin: '8px 0' }}>{failedOrders}</div>
                  <div>Failed</div>
                  <div style={{ color: '#ff4d4f' }}>{totalOrders > 0 ? Math.round((failedOrders/totalOrders)*100) : 0}%</div>
                </Card>
              </Col>
              <Col span={12}>
                <Card style={{ background: '#fafafa', borderColor: '#d9d9d9', borderRadius: 12, textAlign: 'center', borderWidth: 2 }} bordered>
                  <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 32 }} />
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#d9d9d9', margin: '8px 0' }}>{canceledOrders}</div>
                  <div>Canceled</div>
                  <div style={{ color: '#d9d9d9' }}>{totalOrders > 0 ? Math.round((canceledOrders/totalOrders)*100) : 0}%</div>
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title="Top Best-selling Courses"
            style={{ borderRadius: 12, boxShadow: '0 2px 8px #f0f1f2', height: '100%' }}
            bodyStyle={{ minHeight: 450, padding: 3 }}
          >
            {mostPurchasedCourses && mostPurchasedCourses.length > 0 ? (
              <Table
                columns={[
                  {
                    title: 'No',
                    key: 'no',
                    dataIndex: 'no',
                    width: 60,
                    align: 'center',
                    render: (text, record, index) => index + 1,
                  },
                  {
                    title: 'Course Name',
                    dataIndex: 'courseName',
                    key: 'name',
                    render: (text) => <b>{text}</b>,
                  },
                  {
                    title: 'Category',
                    dataIndex: ['category', 'name'],
                    key: 'category',
                    width: 120,
                    render: (text, record) => (
                      <Tooltip title={record.category?.name || ''}>
                        <span>{record.category?.name || ''}</span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'Purchases',
                    dataIndex: 'purchaseCount',
                    key: 'purchaseCount',
                    align: 'center',
                    width: 90,
                    render: (val) => <span style={{ fontWeight: 600, color: '#6c63ff' }}>{val}</span>
                  },
                  {
                    title: 'Revenue',
                    dataIndex: 'totalRevenue',
                    key: 'totalRevenue',
                    align: 'right',
                    width: 120,
                    render: value => value?.toLocaleString('en-US') + ' ₫',
                  },
                ]}
                dataSource={mostPurchasedCourses}
                rowKey="_id"
                size="small"
                pagination={false}
                scroll={{ y: 400 }}
                style={{ borderRadius: 12 }}
                rowClassName={() => 'equal-row-height'}
              />
            ) : (
              <Empty description="No data available" style={{ padding: 30 }} />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StaffStatisticsDashboard; 